import React from 'react';
import Icon from '../../../components/AppIcon';

const StatusTimeline = ({ currentStatus, statusHistory, currentLanguage }) => {
  const translations = {
    en: {
      draft: 'Draft',
      offered: 'Offered',
      accepted: 'Accepted',
      paid: 'Paid',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      completed: 'Completed',
      disputed: 'Disputed',
      cancelled: 'Cancelled',
      timeline: 'Deal Timeline',
      estimatedCompletion: 'Estimated Completion'
    },
    ko: {
      draft: '초안',
      offered: '제안됨',
      accepted: '수락됨',
      paid: '결제됨',
      in_transit: '운송 중',
      delivered: '배송됨',
      completed: '완료됨',
      disputed: '분쟁 중',
      cancelled: '취소됨',
      timeline: '거래 타임라인',
      estimatedCompletion: '예상 완료일'
    }
  };

  const t = translations?.[currentLanguage];

  const statusConfig = {
    draft: { icon: 'FileText', color: 'var(--color-muted-foreground)', bgColor: 'bg-muted' },
    offered: { icon: 'Send', color: 'var(--color-primary)', bgColor: 'bg-primary/10' },
    accepted: { icon: 'Handshake', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    paid: { icon: 'CreditCard', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    in_transit: { icon: 'Plane', color: 'var(--color-primary)', bgColor: 'bg-primary/10' },
    delivered: { icon: 'Package', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    completed: { icon: 'CheckCircle', color: 'var(--color-success)', bgColor: 'bg-success/10' },
    disputed: { icon: 'AlertTriangle', color: 'var(--color-error)', bgColor: 'bg-error/10' },
    cancelled: { icon: 'XCircle', color: 'var(--color-destructive)', bgColor: 'bg-destructive/10' }
  };

  const statuses = ['draft', 'offered', 'accepted', 'paid', 'in_transit', 'delivered', 'completed'];
  const currentIndex = statuses?.indexOf(currentStatus);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString(currentLanguage === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8">
      <h3 className="text-lg md:text-xl lg:text-2xl font-heading font-semibold text-foreground mb-6 md:mb-8">
        {t?.timeline}
      </h3>
      <div className="space-y-4 md:space-y-6">
        {statuses?.map((status, index) => {
          const config = statusConfig?.[status];
          const isCompleted = index <= currentIndex;
          const isCurrent = index === currentIndex;
          const historyItem = statusHistory?.find(h => h?.status === status);

          return (
            <div key={status} className="relative">
              {index < statuses?.length - 1 && (
                <div className={`absolute left-5 md:left-6 top-12 md:top-14 w-0.5 h-full ${
                  isCompleted ? 'bg-primary' : 'bg-border'
                }`} />
              )}
              <div className="flex items-start gap-4 md:gap-6">
                <div className={`relative flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-base ${
                  isCompleted ? config?.bgColor : 'bg-muted'
                } ${isCurrent ? 'ring-4 ring-primary/20' : ''}`}>
                  <Icon 
                    name={config?.icon} 
                    size={20} 
                    color={isCompleted ? config?.color : 'var(--color-muted-foreground)'} 
                  />
                </div>

                <div className="flex-1 min-w-0 pt-1">
                  <div className="flex items-center justify-between gap-4 mb-2">
                    <h4 className={`text-sm md:text-base font-medium ${
                      isCompleted ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {t?.[status]}
                    </h4>
                    {historyItem && (
                      <span className="text-xs md:text-sm text-muted-foreground caption whitespace-nowrap">
                        {formatDate(historyItem?.timestamp)}
                      </span>
                    )}
                  </div>

                  {historyItem?.note && (
                    <p className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                      {historyItem?.note}
                    </p>
                  )}

                  {isCurrent && !historyItem && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                      <span className="text-xs md:text-sm text-primary caption">In Progress</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {currentStatus !== 'completed' && currentStatus !== 'cancelled' && currentStatus !== 'disputed' && (
        <div className="mt-6 md:mt-8 pt-6 md:pt-8 border-t border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm md:text-base text-muted-foreground">{t?.estimatedCompletion}</span>
            <span className="text-sm md:text-base font-medium text-foreground data-text">
              {formatDate('2026-02-25')}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatusTimeline;